// src/pages/guia/GuiaMapa.js
//
// Mapa de zona del Guía: muestra su radio de operación y las tiendas
// afiliadas alrededor. Los marcadores son verdes (Negocio), amarillos
// (Local) o naranjas (360°) según lo que tenga contratado.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Box, Stack, Alert, LinearProgress, Card, CardContent, Chip,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import { GoogleMap, Marker, Circle, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { guia as api } from '../../api/guiaApi';
import { useApp } from '../../context/AppContext';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const CENTRO_DEFAULT = { lat: 7.11935, lng: -73.1227 }; // Bucaramanga

// Iconos por tipo de contratación (colores de la marca)
const COLOR_NEGOCIO = '#0F4C3A';  // verde profundo
const COLOR_LOCAL   = '#F4B23C';  // amarillo maduro
const COLOR_360     = '#E85D3B';  // naranja terracota

function colorAfiliado(a) {
  if (a.contrataHolaVeci && a.contrataPOS) return COLOR_360;
  if (a.contrataPOS) return COLOR_LOCAL;
  return COLOR_NEGOCIO;
}

function etiquetaAfiliado(a) {
  if (a.contrataHolaVeci && a.contrataPOS) return '360°';
  if (a.contrataPOS) return 'Local';
  return 'Negocio';
}

export default function GuiaMapa() {
  const { notificar } = useApp();
  const [perfil, setPerfil] = useState(null);
  const [afiliados, setAfiliados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: API_KEY || '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [p, a] = await Promise.all([api.perfil(), api.afiliados()]);
        setPerfil(p);
        setAfiliados(a || []);
      } catch (e) {
        notificar(e.mensajeUsuario || 'No se pudo cargar el mapa.', 'error');
      } finally { setCargando(false); }
    })();
  }, [notificar]);

  const centro = useMemo(() => {
    if (perfil?.latitud && perfil?.longitud) {
      return { lat: Number(perfil.latitud), lng: Number(perfil.longitud) };
    }
    return CENTRO_DEFAULT;
  }, [perfil]);

  const radioMetros = (perfil?.radioOperacionKm || 2) * 1000;

  const marcadores = afiliados
    .filter(a => a.ubicacion?.latitud && a.ubicacion?.longitud)
    .map(a => ({
      ...a,
      pos: { lat: Number(a.ubicacion.latitud), lng: Number(a.ubicacion.longitud) },
    }));

  if (!API_KEY) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info">
          El mapa no está disponible. Falta configurar la variable de entorno{' '}
          <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> en el frontend.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <MapIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Mapa de tu zona</Typography>
          <Typography variant="body2" color="text.secondary">
            Tu radio de operación y los negocios que has afiliado.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body2">Leyenda:</Typography>
            <Chip size="small" label="Negocio" sx={{ bgcolor: COLOR_NEGOCIO, color: 'white' }} />
            <Chip size="small" label="Local"   sx={{ bgcolor: COLOR_LOCAL,   color: 'black' }} />
            <Chip size="small" label="360°"    sx={{ bgcolor: COLOR_360,     color: 'white' }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Radio: {perfil?.radioOperacionKm || 2} km · Afiliados en el mapa: {marcadores.length}/{afiliados.length}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {cargando && <LinearProgress />}

      {isLoaded && !cargando && (
        <Card>
          <Box sx={{ height: '70vh', minHeight: 480 }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={centro}
              zoom={14}
              options={{
                streetViewControl: false, mapTypeControl: false, fullscreenControl: true,
              }}
            >
              {/* Círculo de radio del Guía */}
              {perfil?.latitud && perfil?.longitud && (
                <Circle
                  center={centro}
                  radius={radioMetros}
                  options={{
                    fillColor: COLOR_NEGOCIO, fillOpacity: 0.08,
                    strokeColor: COLOR_NEGOCIO, strokeOpacity: 0.5, strokeWeight: 2,
                  }}
                />
              )}
              {/* Pin del Guía */}
              {perfil?.latitud && perfil?.longitud && (
                <Marker position={centro}
                        icon={window.google && {
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 10, fillColor: '#0F4C3A', fillOpacity: 1,
                          strokeColor: '#fff', strokeWeight: 3,
                        }}
                        title="Tu ubicación base" />
              )}
              {/* Marcadores de afiliados */}
              {marcadores.map(a => (
                <Marker
                  key={a.empresaId}
                  position={a.pos}
                  onClick={() => setSeleccionado(a)}
                  icon={window.google && {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8, fillColor: colorAfiliado(a), fillOpacity: 1,
                    strokeColor: '#fff', strokeWeight: 2,
                  }}
                />
              ))}
              {seleccionado && (
                <InfoWindow position={seleccionado.pos} onCloseClick={() => setSeleccionado(null)}>
                  <Box sx={{ maxWidth: 220 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {seleccionado.nombreComercial || seleccionado.razonSocial}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {seleccionado.ubicacion?.direccion}
                    </Typography>
                    <Chip size="small" label={etiquetaAfiliado(seleccionado)}
                          sx={{ mt: 1, bgcolor: colorAfiliado(seleccionado),
                                color: colorAfiliado(seleccionado) === COLOR_LOCAL ? 'black' : 'white' }} />
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
          </Box>
        </Card>
      )}

      {!cargando && marcadores.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Aún no hay tiendas con ubicación registrada. A medida que tus afiliados
          completen su dirección con GPS, aparecerán en el mapa.
        </Alert>
      )}
    </Container>
  );
}
